<script setup lang="ts">
import { computed, inject, watch, watchEffect } from "vue";

import * as tmi from "@/constants";

import { useProductFilter } from "@/composables/filters/product-filter";
import { useProductsStore } from "@/stores/Products";
import type { PagingFilter } from "@/composables/filters/paging-filter";
import { pagingFilterSymbol } from "@/plugins/ContentFiltersPlugin";
import { storeToRefs } from "pinia";
import { useUrlSearchParams } from '@vueuse/core';

const urlParams = useUrlSearchParams();
const { hasProductsToFilter, includeProductFilter } = useProductFilter();
const productsStore = useProductsStore();
const { subscribedProducts } = storeToRefs( productsStore );
const selectionOptionsForProductsFilter = computed< any[] >( () => [ ...subscribedProducts.value, productsStore.productBySlug( tmi.TmiPublicContentPermissionKey ) ] );

const pagingFilter = inject< PagingFilter >( pagingFilterSymbol ) as PagingFilter;
function resetPageNumber() {
  pagingFilter?.resetPageNumber();
}

// Single product:
//        http://localhost:5173/learning/articles?product=checkpoint-trader

// Multiple products:
//        http://localhost:5173/learning/articles?product=small-cap-cash-cows&product=true-sovereign-profits

// Using both product & category parameters:
//        http://localhost:5173/learning/articles?product=mega-trend-profit-club&category=weekly-update
watch(
    () => productsStore.allProducts.length, // Reacts to changes in product list
    (products) => {
        if (products > 0 && urlParams.product) { // Ensure products exist before proceeding
            const productCodes = Array.isArray(urlParams.product) ? urlParams.product : [urlParams.product];

            productCodes.forEach((productCode) => {
                const product = productsStore.productByProductCode(productCode);
                if (product) {
                includeProductFilter.value.push(product);
                }
            });
        }
    },
    { immediate: true } // Runs immediately if products are already loaded
);
const stopWatcher = watchEffect((onCleanup) => {
  // Cleanup function to stop watching after first run
    onCleanup(() => stopWatcher());
});
</script>

<template>
<MultiSelect
    v-if="hasProductsToFilter"
    v-model="includeProductFilter"
    filter
    :options="selectionOptionsForProductsFilter"
    optionLabel="name"
    @change="resetPageNumber"
    dataKey="id"
    placeholder="Filter by Products"
    class="w-full">

    <template #option="slotProps">
        <div class="flex items-center">
            <img :alt="slotProps.option.name" :src="slotProps.option.favicon" class="mr-2" style="width: 18px" />
            <div>{{ slotProps.option.name }}</div>
        </div>
    </template>
</MultiSelect>
</template>
